import React from 'react'

import type { IconType } from 'react-icons'
import { twMerge } from 'tailwind-merge'

interface SurfaceProps {
  children: React.ReactNode
  className?: string
}

const SurfaceRoot: React.FC<SurfaceProps> = ({ children, className }) => {
  return (
    <div
      className={twMerge(
        'bg-white dark:bg-primary-900 rounded-lg shadow-sm overflow-hidden border border-primary-200 dark:border-primary-800',
        className,
      )}
    >
      {children}
    </div>
  )
}

interface SurfaceContentProps {
  children: React.ReactNode
  className?: string
}

const SurfaceContent: React.FC<SurfaceContentProps> = ({
  children,
  className,
}) => {
  return <div className={twMerge('p-4 w-full', className)}>{children}</div>
}

interface SurfaceHeaderTitleProps {
  children: React.ReactNode
  className?: string
}

const SurfaceHeaderTitle: React.FC<SurfaceHeaderTitleProps> = ({
  children,
  className,
}) => {
  return (
    <h2
      className={twMerge(
        'text-md font-semibold text-primary-900 dark:text-primary-100',
        className,
      )}
    >
      {children}
    </h2>
  )
}

interface SurfaceHeaderIconProps {
  icon: IconType
  size?: number
  className?: string
}

const SurfaceHeaderIcon: React.FC<SurfaceHeaderIconProps> = ({
  icon: Icon,
  size = 18,
  className,
}) => {
  return (
    <div
      className={twMerge('text-primary-700 dark:text-primary-300', className)}
    >
      <Icon size={size} />
    </div>
  )
}

interface SurfaceHeaderMenuProps {
  children: React.ReactNode
  className?: string
}

const SurfaceHeaderMenu: React.FC<SurfaceHeaderMenuProps> = ({
  children,
  className,
}) => {
  return (
    <div
      className={twMerge('text-primary-700 dark:text-primary-300', className)}
    >
      {children}
    </div>
  )
}

interface SurfaceHeaderProps {
  children: React.ReactNode
  className?: string
}

const SurfaceHeaderRoot: React.FC<SurfaceHeaderProps> = ({
  children,
  className,
}) => {
  const [icon, title, menu] = React.Children.toArray(children)
  return (
    <div
      className={twMerge(
        'p-4 bg-white dark:bg-primary-900 border-b border-primary-200 dark:border-primary-800',
        className,
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-2">
          <span className="mt-[3px]">{icon}</span>
          {title}
        </div>
        <div className="mt-[4px]">{menu}</div>
      </div>
    </div>
  )
}

interface PlaceholderSurfaceProps {
  children: React.ReactNode
  className?: string
}

const PlaceholderSurface: React.FC<PlaceholderSurfaceProps> = ({
  children,
  className,
}) => {
  return (
    <div
      className={twMerge(
        'flex items-center justify-center py-[40px] px-4 w-full text-[13px] font-medium text-primary-500 dark:text-primary-400 bg-primary-100 dark:bg-primary-800/50 rounded-md',
        className,
      )}
    >
      {children}
    </div>
  )
}

PlaceholderSurface.displayName = 'Home.Surface.Placeholder'
SurfaceHeaderRoot.displayName = 'Home.Surface.Header'
SurfaceHeaderTitle.displayName = 'Home.Surface.Header.Title'
SurfaceHeaderIcon.displayName = 'Home.Surface.Header.Icon'
SurfaceHeaderMenu.displayName = 'Home.Surface.Header.Menu'

const SurfaceHeader = Object.assign(SurfaceHeaderRoot, {
  Title: SurfaceHeaderTitle,
  Icon: SurfaceHeaderIcon,
  Menu: SurfaceHeaderMenu,
})

export const Surface = Object.assign(SurfaceRoot, {
  Header: SurfaceHeader,
  Content: SurfaceContent,
  Placeholder: PlaceholderSurface,
})

SurfaceHeader.displayName = 'Home.Surface.Header'
Surface.displayName = 'Home.Surface'
